import React from 'react'

import lodash from 'lodash'

import { Icon as IconifyIcon } from "@iconify/react"
import Button from '@mui/material/Button'
import LogErrorDetails from './LogErrorDetails.jsx'

import ErrorView from '../../error/ErrorView.jsx'

import './ProcessingLog.scss'

export default function ProcessingLog({ importInstance }) {

  const entries = importInstance?.processing?.processedEntries;

  const resultCount = React.useMemo(() => {
    // If we have entries with details, use them
    if (entries != null && lodash.isArray(entries) && entries.length > 0) {
      const totalProcessed = entries.length;

      // Helper function to check if an entry has validation errors
      const hasValidationErrors = (item) => {
        const hasFieldErrors = item?.report?.fieldErrors?.length > 0;
        const hasTopLevelErrors = item?.report?.topLevelErrors?.length > 0;
        const hasImportErrors = item?.report?.importErrors?.length > 0;
        return hasFieldErrors || hasTopLevelErrors || hasImportErrors;
      };

      // Count errors: either failed import state OR validation errors present
      const errorCount = entries.reduce((sum, item) => {
        if (item?.action === 'DELETE') return sum; // Don't count deletes as errors here
        const hasFailed = item?.state?.importSuccessful === false;
        const hasErrors = hasValidationErrors(item);
        return sum + ((hasFailed || hasErrors) ? 1 : 0);
      }, 0);

      const deleteFailures = entries.reduce((sum, item) =>
        sum + ((item?.state?.importSuccessful === false && item?.action === 'DELETE') ? 1 : 0), 0);
      const deletedSuccessful = entries.reduce((sum, item) =>
        sum + ((item?.state?.importSuccessful === true && item?.action === 'DELETE') ? 1 : 0), 0);

      return {
        success: totalProcessed - errorCount - deleteFailures - deletedSuccessful,
        error: errorCount,
        deleteFailures: deleteFailures,
        deleted: deletedSuccessful
      }
    } else {
      // Fallback: if processedEntries is empty, use progress.total
      // This happens when import was successful with no errors to report
      const totalProcessed = importInstance?.processing?.excel?.progress?.total || 0;
      return {
        success: totalProcessed,
        error: 0,
        deleteFailures: 0,
        deleted: 0
      }
    }
  }, [entries, importInstance?.processing?.excel?.progress?.total])

  const renderDetails = () => {
    if (entries == null || lodash.isArray(entries) === false) return null

    let list = []
    let i = 1
    for (let entry of entries) {
      let errors = []

      for (let fieldError of entry.report.fieldErrors || []) {
        errors.push(
          <div className="error" key={`field-${fieldError.field}-${i}`}>
            <IconifyIcon className="icon" icon="ph:caret-double-right-duotone" />
            <span>
                            <b>{fieldError.field}:</b><br />
                            <div className="formatted-message">
                                {fieldError.message.split('. ').map((part, idx) => (
                                  <p key={idx}>{part.trim()}</p>
                                ))}
                            </div>
              {fieldError.cause &&
                <div className="cause">
                  <b>CAUSED BY</b><br />
                  <b>Type:</b> {fieldError.cause.name}<br />
                  <b>Message:</b> {fieldError.cause.message}<br />
                  <LogErrorDetails>
                    <ErrorView
                      title={fieldError.message}
                      error={fieldError.cause}
                    />
                  </LogErrorDetails>
                </div>
              }
                        </span>
          </div>
        )
      }

      for (let topLevelError of entry.report.topLevelErrors || []) {
        errors.push(
          <div className="error" key={`top-${i}`}>
            <IconifyIcon className="icon" icon="ph:caret-double-right-duotone" />
            <span>
                            <b>Top-Level Error:</b><br />
                            <div className="formatted-message">
                                {topLevelError.message.split('. ').map((part, idx) => (
                                  <p key={idx}>{part.trim()}</p>
                                ))}
                            </div>
                        </span>
          </div>
        )
      }

      for (let importError of entry.report.importErrors || []) {
        errors.push(
          <div className="error" key={`import-${i}`}>
            <IconifyIcon className="icon" icon="ph:caret-double-right-duotone" />
            <span>
                            <b>Import Error:</b><br />
                            <div className="formatted-message">
                                {importError.message.split('. ').map((part, idx) => (
                                  <p key={idx}>{part.trim()}</p>
                                ))}
                            </div>
                        </span>
          </div>
        )
      }

      const caseId = entry?.genericCase?.internalCaseId
      const excelRow = entry?.excelRowNumber
      let entryLabel
      if (caseId && excelRow) {
        entryLabel = `Case "${caseId}" (Excel Zeile ${excelRow})`
      } else if (caseId) {
        entryLabel = `Case "${caseId}"`
      } else if (excelRow) {
        entryLabel = `Excel Zeile ${excelRow}`
      } else {
        entryLabel = `Eintrag ${i}`
      }

      list.push(
        <div className="entry-detail" key={`entry-${i}`}>
          <div className="label">{entryLabel}:</div>
          {errors.length === 0 && entry.state?.importSuccessful === true &&
            <div className="success">
              <IconifyIcon className="icon" icon="ph:caret-double-right-duotone" />
              <span><b>importiert</b></span>
            </div>
          }
          {errors}
        </div>
      )
      i++
    }


    return list
  }

  const downloadReport = (format) => {
    const importName = importInstance?.name || 'import'
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
    const filename = `error-report_${importName}_${timestamp}.${format}`

    let content, mimeType

    if (format === 'json') {
      const report = {
        importName,
        exportedAt: new Date().toISOString(),
        summary: resultCount,
        entries: (entries || []).map(entry => {
          const caseId = entry?.genericCase?.internalCaseId
          const excelRow = entry?.excelRowNumber
          return {
            caseId: caseId || null,
            excelRow: excelRow || null,
            action: entry?.action || null,
            importSuccessful: entry?.state?.importSuccessful ?? null,
            fieldErrors: entry?.report?.fieldErrors || [],
            topLevelErrors: entry?.report?.topLevelErrors || [],
            importErrors: entry?.report?.importErrors || []
          }
        })
      }
      content = JSON.stringify(report, null, 2)
      mimeType = 'application/json'
    } else {
      const lines = []
      lines.push(`Error Report: ${importName}`)
      lines.push(`Exported: ${new Date().toLocaleString()}`)
      lines.push('')
      lines.push('=== Summary ===')
      lines.push(`Imported: ${resultCount.success}`)
      lines.push(`Deleted: ${resultCount.deleted}`)
      lines.push(`Errors: ${resultCount.error}`)
      lines.push(`Delete Failures: ${resultCount.deleteFailures}`)
      lines.push('')
      lines.push('=== Details ===')
      for (const entry of (entries || [])) {
        const caseId = entry?.genericCase?.internalCaseId
        const excelRow = entry?.excelRowNumber
        let label = caseId && excelRow
          ? `Case "${caseId}" (Excel row ${excelRow})`
          : caseId ? `Case "${caseId}"` : excelRow ? `Excel row ${excelRow}` : 'Entry'
        lines.push('')
        lines.push(`--- ${label} ---`)
        lines.push(`Action: ${entry?.action || 'N/A'} | Successful: ${entry?.state?.importSuccessful ?? 'N/A'}`)
        for (const e of entry?.report?.fieldErrors || []) {
          lines.push(`  [Field Error] ${e.field}: ${e.message}`)
          if (e.cause) lines.push(`    Caused by: ${e.cause.name}: ${e.cause.message}`)
        }
        for (const e of entry?.report?.topLevelErrors || []) {
          lines.push(`  [Top-Level Error] ${e.message}`)
        }
        for (const e of entry?.report?.importErrors || []) {
          lines.push(`  [Import Error] ${e.message}`)
        }
      }
      content = lines.join('\n')
      mimeType = 'text/plain'
    }

    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    importInstance?.processing?.excel?.state === 'FINISHED' ?
      <div className="processing-log">
        <div className="box">
          <div className="label">Zusammenfassung</div>

          {/* Successful imports */}
          <p>
            Importierte Varianten: <b>{resultCount.success}</b>
          </p>
          {entries && entries.length > 0 && (
            <ul className="import-summary-list">
              {entries
                .filter(e => e?.state?.importSuccessful === true && e?.action !== 'DELETE')
                .map((entry, idx) => (
                  <li key={`success-${idx}`}>
                    <IconifyIcon className="icon" icon="ph:check-circle-duotone" />
                    <span>
                                            <b>Case ID:</b> {entry?.genericCase?.internalCaseId || "N/A"} | <b>HGVS:</b> {entry?.excel?.HGVS_gDNA?.value || "N/A"}
                                        </span>
                  </li>
                ))
              }
            </ul>
          )}

          {/* Deleted entries */}
          <p>
            Gelöschte Varianten: <b>{resultCount.deleted}</b>
          </p>
          {entries && entries.length > 0 && (
            <ul className="import-summary-list deleted-list">
              {entries
                .filter(e => e?.action === 'DELETE' && e?.state?.importSuccessful === true)
                .map((entry, idx) => (
                  <li key={`deleted-${idx}`}>
                    <IconifyIcon className="icon" icon="ph:trash-duotone" />
                    <span>
                                            <b>Case ID:</b> {entry?.genericCase?.internalCaseId || "N/A"} | <b>HGVS:</b> {entry?.excel?.HGVS_gDNA?.value || "N/A"}
                                        </span>
                  </li>
                ))
              }
            </ul>
          )}

          {/* Failed imports (not deletes) */}
          <p>
            Fehlerhafte Varianten: <b>{resultCount.error}</b>
          </p>
          {entries && entries.length > 0 && (
            <ul className="import-summary-list error-list">
              {entries
                .filter(e => e?.state?.importSuccessful === false && e?.action !== 'DELETE')
                .map((entry, idx) => (
                  <li key={`error-${idx}`}>
                    <IconifyIcon className="icon" icon="ph:x-circle-duotone" />
                    <span>
                                            <b>Case ID:</b> {entry?.genericCase?.internalCaseId || "N/A"} | <b>HGVS:</b> {entry?.excel?.HGVS_gDNA?.value || "N/A"}
                                        </span>
                  </li>
                ))
              }
            </ul>
          )}

          {/* ❗ Failed DELETEs */}
          <p>
            Fehlgeschlagene Löschungen: <b>{resultCount.deleteFailures}</b>
          </p>
          {entries && entries.length > 0 && (
            <ul className="import-summary-list delete-error-list">
              {entries
                .filter(e => e?.state?.importSuccessful === false && e?.action === 'DELETE')
                .map((entry, idx) => (
                  <li key={`delete-error-${idx}`}>
                    <IconifyIcon className="icon" icon="ph:warning-duotone" />
                    <span>
                                            <b>Case ID:</b> {entry?.genericCase?.internalCaseId || "N/A"} | <b>Grund:</b> {entry?.report?.importErrors?.[0]?.message || "Unbekannter Fehler"}
                                        </span>
                  </li>
                ))
              }
            </ul>
          )}

          <div className="download-buttons">
            <Button
              size="small"
              variant="outlined"
              startIcon={<IconifyIcon icon="ph:file-text-duotone" />}
              onClick={() => downloadReport('txt')}
            >
              Download TXT
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<IconifyIcon icon="ph:file-code-duotone" />}
              onClick={() => downloadReport('json')}
            >
              Download JSON
            </Button>
          </div>
        </div>

        {/* Detailed Error Views */}
        <div className="box scrollable-box">
          <div className="label">Details</div>
          <div className="scroll-container">
            {renderDetails()}
          </div>
        </div>
      </div>
      : null
  )
}
